import { useRef } from "react";
import { Link } from "react-router-dom";
import { Volume2, VolumeX } from "lucide-react";
import creator from "../../assets/creator.png";
import creatorDemo from "../../assets/creatorDemo.png";
import youtuberVideo from "../../assets/Youtuber.mp4";
import youtuberPoster from "../../assets/Youtube.png";
import vloggerVideo from "../../assets/Vlogger.mp4";
import vloggerPoster from "../../assets/vlogger.png";
import podcasterVideo from "../../assets/Podcaster.mp4";
import podcasterPoster from "../../assets/podcaster.png";
import moreVideo from "../../assets/More.mp4";
import morePoster from "../../assets/more.png";
import beforeVideo from "../../assets/Before.mp4";
import afterVideo from "../../assets/Podcast_Intro.mp4";
import thumbsUp from "../../assets/thumbs-up-3d 1.png";
import useTypewriter from "../../hooks/useTypewriter";
import useOnScreen from "../../hooks/useOnScreen";
import useVideoMute from "../../hooks/useVideoMute";
import CreatorCard from "./CreatorCard";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Carousel from "./Carousel";

const Creators = () => {
  const ref = useRef(null);
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: demoSectionRef, isVisible: demoSectionVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: beforeAfterRef, isVisible: beforeAfterVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: thumbsRef, isVisible: thumbsVisible } = useScrollAnimation({ threshold: 0.5 });

  const isVisible = useOnScreen(ref, { threshold: 0.5 });

  // Video mute hooks for Before and After videos
  const beforeVideoMute = useVideoMute('before-video');
  const afterVideoMute = useVideoMute('after-video');

  const fullSubtitle = "Just drag, drop, and customize.";
  const typedSubtitle = useTypewriter(fullSubtitle, 50, isVisible);

  return (
    <div className="bg-ag-black-900 w-full relative overflow-hidden">

      {/* Content wrapper with relative positioning */}
      <div className="relative z-10">
        <div className="pt-8 sm:pt-12 md:pt-16 lg:pt-20 xl:pt-22 2xl:pt-24 w-[95%] sm:w-[92%] md:w-[90%] mx-auto flex flex-col px-2 sm:px-4 md:px-8 lg:px-12 xl:px-16 2xl:px-20 gap-4 sm:gap-5 md:gap-6">
          <p
            ref={titleRef}
            className={`font-sourceSans font-normal text-base sm:text-lg md:text-xl lg:text-2xl mb-4 sm:mb-5 md:mb-6 lg:mb-8 text-center text-white transition-all duration-700 ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
          >
            Built for creators
          </p>

          <div
            ref={cardsRef}
            className="p-2 sm:p-3 h-fit rounded-2xl sm:rounded-3xl grid grid-cols-1 sm:grid-cols-2 items-center justify-center gap-2 sm:gap-3"
          >
            <div className={`transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{ transitionDelay: '0ms' }}>
              <CreatorCard
                media={youtuberVideo}
                poster={youtuberPoster}
                alt="YouTubers"
                buttonText="YouTubers"
                isVideo={true}
              />
            </div>
            <div className={`transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{ transitionDelay: '150ms' }}>
              <CreatorCard
                media={vloggerVideo}
                poster={vloggerPoster}
                alt="Vloggers"
                buttonText="Vloggers"
                isVideo={true}
              />
            </div>
            <div className={`transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{ transitionDelay: '300ms' }}>
              <CreatorCard
                media={podcasterVideo}
                poster={podcasterPoster}
                alt="Podcasters"
                buttonText="Podcasters"
                isVideo={true}
              />
            </div>
            <div className={`transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{ transitionDelay: '450ms' }}>
              <CreatorCard
                media={moreVideo}
                poster={morePoster}
                alt="More creators"
                buttonText="+More"
                isVideo={true}
              />
            </div>
          </div>
          <p className={`font-sourceSans font-normal text-base sm:text-lg md:text-xl lg:text-2xl mb-4 sm:mb-5 md:mb-6 lg:mb-8 text-center text-white transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`} style={{ transitionDelay: '600ms' }}>
            Building the next generation creative content
          </p>
        </div>

        {/* Demo section with slide animations */}
        {/* <div
          ref={demoSectionRef}
          className="relative overflow-hidden bg-white mt-8 sm:mt-12 md:mt-16 lg:mt-20 xl:mt-22 2xl:mt-24 py-12 sm:py-16 md:py-20 lg:py-24 xl:py-28 2xl:py-32 flex md:flex-row flex-col gap-4 md:gap-0 w-full items-center justify-between px-4 sm:px-6 md:px-0"
        > */}
        {/* Thick stroke with blur at the top */}
        {/* <div className="absolute top-[-15px] sm:top-[-20px] md:top-[-25px] lg:top-[-30px] left-0 right-0 h-[40px] sm:h-[50px] md:h-[60px] lg:h-[80px] bg-ag-green blur-xl"></div>
          <div className={`flex flex-col gap-3 sm:gap-4 md:gap-5 lg:gap-6 md:ml-8 lg:ml-12 xl:ml-16 2xl:ml-20 transition-all duration-700 ${demoSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}>
            <p className="font-sourceSans font-black tracking-tighter text-center md:text-start text-ag-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl">
              No <span className="font-sourceSans font-light italic">animation</span>
              <span className="block">experience needed.</span>
            </p>
            <p
              ref={ref}
              className="font-medium text-center md:text-start text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-3xl text-ag-black-300"
            >
              {typedSubtitle}
              {isVisible && typedSubtitle.length < fullSubtitle.length && (
                <span className="animate-pulse text-ag-green">|</span>
              )}
            </p>
          </div>

          <div className={`w-full md:w-auto max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl transition-all duration-700 ${demoSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`} style={{ transitionDelay: '200ms' }}>
            <img src={creatorDemo} alt="demo" className="w-full h-auto hover:scale-105 transition-transform duration-500" />
          </div>
        </div>*/}
      </div>

      <Carousel />

      {/* Before/After section with animations */}
      <div
        ref={beforeAfterRef}
        className="bg-ag-black-900 py-6 sm:py-8 md:py-12 lg:py-16 xl:py-20 2xl:pt-24 flex flex-col items-center justify-center px-3 sm:px-4"
      >
        {/* Two videos side by side with labels centered below each video */}
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 md:gap-8 items-start justify-center mb-4 sm:mb-5 md:mb-6 w-full max-w-9xl">
          {/* Before Video with label below */}
          <div className={`flex flex-col items-center w-full md:w-[45%] transition-all duration-700 ${beforeAfterVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}>
            <div className="relative rounded-xl sm:rounded-2xl border-2 sm:border-3 md:border-4 border-black overflow-hidden w-full aspect-[7/4] hover:scale-[1.02] transition-transform duration-500 hover:shadow-xl group">
              <video
                ref={beforeVideoMute.videoRef}
                src={beforeVideo}
                autoPlay
                loop
                muted={beforeVideoMute.isMuted}
                playsInline
                className="w-full h-full object-cover"
              />
              {/* Unmute button - top right corner */}
              <button
                onClick={beforeVideoMute.toggleMute}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 sm:p-2.5 md:p-3 transition-all duration-300 hover:scale-110 z-10 backdrop-blur-sm"
                aria-label={beforeVideoMute.isMuted ? "Unmute video" : "Mute video"}
              >
                {beforeVideoMute.isMuted ? (
                  <VolumeX className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                ) : (
                  <Volume2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                )}
              </button>
            </div>
            <p className="font-sourceSans text-base sm:text-lg md:text-xl lg:text-2xl font-medium text-ag-black-300 mt-2 md:mt-4 text-center">Before</p>
          </div>

          {/* After Video with label below */}
          <div className={`flex flex-col items-center w-full md:w-[45%] transition-all duration-700 ${beforeAfterVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`} style={{ transitionDelay: '200ms' }}>
            <div className="relative rounded-xl sm:rounded-2xl border-2 sm:border-3 md:border-4 border-black overflow-hidden w-full aspect-[7/4] hover:scale-[1.02] transition-transform duration-500 hover:shadow-xl group">
              <video
                ref={afterVideoMute.videoRef}
                src={afterVideo}
                autoPlay
                loop
                muted={afterVideoMute.isMuted}
                playsInline
                className="w-full h-full object-cover"
              />
              {/* Unmute button - top right corner */}
              <button
                onClick={afterVideoMute.toggleMute}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 sm:p-2.5 md:p-3 transition-all duration-300 hover:scale-110 z-10 backdrop-blur-sm"
                aria-label={afterVideoMute.isMuted ? "Unmute video" : "Mute video"}
              >
                {afterVideoMute.isMuted ? (
                  <VolumeX className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                ) : (
                  <Volume2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                )}
              </button>
            </div>
            <p className="font-sourceSans text-base sm:text-lg md:text-xl lg:text-2xl font-medium text-ag-black-300 mt-2 md:mt-4 text-center">After</p>
          </div>
        </div>

        {/* Button row - centered */}
        <div className={`flex flex-row items-center justify-center w-full transition-all duration-700 ${beforeAfterVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`} style={{ transitionDelay: '400ms' }}>
          <Link to="/templates">
            <button className="bg-ag-green hover:bg-ag-green/90 px-3 sm:px-4 md:px-5 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3 text-black rounded-lg transition-all duration-300 text-base sm:text-lg md:text-xl lg:text-2xl font-sourceSans tracking-tighter font-bold hover:scale-105 hover:shadow-lg hover:shadow-ag-green/30 btn-press ripple-effect">
              Try for free
            </button>
          </Link>
        </div>
      </div>

      {/* Thumbs up section with bounce animation */}
      <div
        ref={thumbsRef}
        className="flex flex-col items-center justify-center py-4 sm:py-6 md:py-8"
      >
        <img
          src={thumbsUp}
          alt="Hand cursor"
          className={`w-16 sm:w-20 md:w-24 lg:w-28 pointer-events-none transition-all duration-700 ${thumbsVisible ? "opacity-100 scale-100" : "opacity-0 scale-50"
            }`}
          style={{ animation: thumbsVisible ? 'floatGentle 4s ease-in-out infinite' : 'none' }}
        />
        <p className={`font-sourceSans font-normal text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center text-ag-black-300 px-4 transition-all duration-700 ${thumbsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`} style={{ transitionDelay: '200ms' }}>
          Make your content stand out, stylish and vibey
          <span className="block">with your audience</span>
        </p>
      </div>

      {/* Marquee text section */}
      <div className="w-full bg-black overflow-hidden">
        <div className="mx-auto w-full max-w-8xl px-3 sm:px-4 py-4 sm:py-5 md:py-6 lg:py-7 xl:py-8">
          <div className="overflow-hidden whitespace-nowrap">
            <p className="inline-block text-center font-sourceSans text-sm sm:text-base md:text-xl lg:text-2xl xl:text-3xl 2xl:text-5xl font-black text-ag-green animate-marquee hover:animate-none cursor-default">
              ENDLESS LIBRARY · MAKE CONTENT ON THE GO · ENDLESS LIBRARY · ENDLESS LIBRARY · MAKE CONTENT ON THE GO · ENDLESS LIBRARY&nbsp;&nbsp;&nbsp;&nbsp;
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Creators;
